// The Storm Mode arrival alert indicator (SMS Phase 5). What the driver reads
// on a stop once Navigate or On my way has been tapped: whether the customer
// was told, how, and when (in the company's time zone), or why not.
//
// The server's state is arrival_alert_sent_at, arrival_alert_channel and
// arrival_alert_reason on each stop of GET /api/routes/:id, and the same
// fields in the POST /api/route-stops/:stopId/on-my-way answer.

import { inZone } from './companyDate';

const NONE_REASONS = {
  no_consent: 'no text consent',
  no_contact: 'no contact',
  sms_off: 'texting off',
  do_not_serve: 'do not serve',
};

// "7:42 AM" in the company's zone (Chicago when none is given).
export const companyClock = (ts, timezone) => {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', inZone(timezone, { hour: 'numeric', minute: '2-digit' }));
};

/**
 * @param {object} stop a stop with the arrival fields
 * @param {boolean} sending a request for this stop is in flight
 * @param {string} [timezone] the company's IANA zone (user.timezone)
 * @returns {{text: string, tone: 'pending'|'sent'|'none'|'failed'} | null}
 */
export const arrivalIndicator = (stop, sending = false, timezone) => {
  if (sending) return { text: 'Sending...', tone: 'pending' };
  if (!stop || !stop.arrival_alert_sent_at) return null;
  if (stop.arrival_alert_reason === 'send_failed') return { text: 'Notify failed', tone: 'failed' };
  const at = companyClock(stop.arrival_alert_sent_at, timezone);
  if (stop.arrival_alert_channel === 'sms') return { text: `Texted ${at}`, tone: 'sent' };
  if (stop.arrival_alert_channel === 'email') return { text: `Emailed ${at}`, tone: 'sent' };
  const why = NONE_REASONS[stop.arrival_alert_reason];
  return { text: why ? `Not notified: ${why}` : 'Not notified', tone: 'none' };
};

/**
 * The skip notice chip (migration 114), beside the arrival chip: whether a
 * customer already alerted was told their stop was skipped, by the alert's
 * channel. From skip_notice_sent_at and skip_notice_channel on GET
 * /api/routes/:id (and the stop PUT's answer). Only on a skipped stop: one
 * un-skipped afterwards keeps the record but shows no chip.
 * @returns {{text: string, tone: 'sent'|'failed'} | null}
 */
export const skipIndicator = (stop, timezone) => {
  if (!stop || stop.status !== 'skipped' || !stop.skip_notice_sent_at) return null;
  if (stop.skip_notice_channel === 'failed') return { text: 'Skip notice failed', tone: 'failed' };
  const at = companyClock(stop.skip_notice_sent_at, timezone);
  if (stop.skip_notice_channel === 'sms') return { text: `Skip texted ${at}`, tone: 'sent' };
  if (stop.skip_notice_channel === 'email') return { text: `Skip emailed ${at}`, tone: 'sent' };
  return null;
};

// Whether a pending stop still offers On my way.
export const canSendArrival = (route, stop) => Boolean(route && route.arrival_alerts_enabled)
  && Boolean(stop) && stop.status !== 'completed' && stop.status !== 'skipped'
  && !stop.arrival_alert_sent_at;

/**
 * The stops one on-my-way answer reports: the tapped stop, then window[] (the
 * stops alerted ahead of it, migration 108; absent at "Next stop only").
 * @returns {Array<object>} entries shaped {stop_id, arrival_alert_sent_at, ...}
 */
export const tapArrivalEntries = (stopId, answer) => (answer
  ? [{ stop_id: stopId, ...answer }, ...(Array.isArray(answer.window) ? answer.window : [])]
  : []);

/**
 * Merges arrival states into a route's stops, so the indicators show without
 * a reload: the on-my-way entries (tapArrivalEntries), or arrival_window[]
 * from the stop PUT that completed or skipped a stop. An entry with no
 * arrival_alert_sent_at (an error, alerts off) changes nothing, and neither
 * does an already_sent one, which would otherwise clear a stored reason.
 * @returns {Array<object>} the stops, the same array when nothing changed
 */
export const mergeArrivalStates = (stops, entries) => {
  const byId = new Map((entries || [])
    .filter((e) => e && e.arrival_alert_sent_at && e.reason !== 'already_sent')
    .map((e) => [Number(e.stop_id), e]));
  if (!stops || byId.size === 0) return stops;
  return stops.map((s) => {
    const e = byId.get(Number(s.id));
    return e ? {
      ...s,
      arrival_alert_sent_at: e.arrival_alert_sent_at,
      arrival_alert_channel: e.arrival_alert_channel,
      arrival_alert_reason: e.arrival_alert_reason || null,
    } : s;
  });
};
